import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Camera, Download, AlertCircle, Image as ImageIcon } from 'lucide-react';

export default function GalleryView() {
  const { galleryId } = useParams<{ galleryId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchGallery() {
      if (!galleryId) return;
      try {
        const bookingsRef = collection(db, 'bookings');
        const q = query(bookingsRef, where('galleryId', '==', galleryId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          const expiresAt = new Date(docData.galleryExpiresAt);
          
          if (new Date() > expiresAt) {
            setError('This gallery link has expired. Links remain active for 7 days post-session.');
          } else {
            setBooking(docData);
          }
        } else {
          setError('Gallery not found or invalid link.');
        }
      } catch (err) {
        console.error("Error fetching gallery:", err);
        setError('Error fetching gallery.');
      } finally {
        setLoading(false);
      }
    }
    fetchGallery();
  }, [galleryId]);

  if (loading) return <div className="min-h-screen pt-32 flex items-center justify-center p-4">Loading Gallery...</div>;

  return (
    <div className="pt-32 pb-24 bg-ows-dark min-h-screen">
      <Helmet>
        <title>Your Post-Session Gallery | Open Water Syndicate</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 text-ows-teal mb-6">
            <Camera className="h-6 w-6" />
            <span className="text-xs font-bold tracking-[0.5em] uppercase font-mono text-white">Post-Session Archive</span>
          </div>
          <h1 className="text-4xl font-display font-light uppercase tracking-widest text-white mb-6">
            {booking ? `${booking.clientName}'s Gallery` : 'Gallery'}
          </h1>
        </div>

        {error ? (
          <div className="p-6 bg-red-900/20 text-red-400 rounded-3xl text-center border border-red-500/20 flex flex-col items-center justify-center gap-4 text-sm uppercase tracking-widest font-bold max-w-xl mx-auto">
            <AlertCircle className="h-10 w-10 mb-2 shadow-2xl" /> 
            {error}
            <button onClick={() => navigate('/')} className="mt-4 px-6 py-3 bg-white text-ows-dark rounded-full font-bold uppercase tracking-widest text-[10px] hover:bg-ows-teal hover:text-white transition-all">
              Return Home
            </button>
          </div>
        ) : booking && (
          <div className="space-y-12">
            <div className="text-center text-white/60 mb-12">
               <p className="text-sm">Session: {booking.serviceName} • {booking.date}</p>
               <p className="text-[10px] mt-2 uppercase tracking-widest text-ows-teal">Link expires: {new Date(booking.galleryExpiresAt).toLocaleDateString()}</p>
            </div>

            <div className="bg-white/5 p-16 rounded-[3rem] border border-white/10 text-center flex flex-col items-center justify-center opacity-80 shadow-2xl backdrop-blur-sm max-w-4xl mx-auto">
              <ImageIcon className="h-16 w-16 text-white/20 mb-6" />
              <p className="text-white mb-8 font-light text-xl">The Guide is currently processing your high-res photos.</p>
              <p className="text-white/60 max-w-md mx-auto text-sm leading-relaxed">
                Check back soon. Once uploaded by the crew, your images will appear here for direct download.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
